import { requestNewPassword } from "@/features/authentication/service.server";
import { requestNewPasswordInputSchema } from "@/features/authentication/validation.server";
import { PublicLayout } from "@/features/navigation/components/public-layout.component";
import { formatErrorMessagesForErrorResult } from "@/utils/format-error-messages-for-error-result";
import {
	Alert,
	Anchor,
	Box,
	Button,
	Container,
	Group,
	Image,
	Paper,
	Text,
	TextInput,
} from "@mantine/core";
import type { ActionArgs } from "@remix-run/node";
import { json } from "@remix-run/node";
import { Form, Link, useActionData, useNavigation } from "@remix-run/react";
import { IconAlertCircle } from "@tabler/icons-react";
import { inputFromForm } from "domain-functions";

export default function PasswordReset() {
	const actionData = useActionData<typeof action>();
	const navigation = useNavigation();

	return (
		<PublicLayout>
			<Container size="xs" p="md">
				{actionData?.success ? (
					<Paper
						withBorder
						py="xl"
						px={{
							base: "md",
							md: 80,
						}}
					>
						<Image
							src="/images/DT_logo_circle.svg"
							alt="Designers Trust logo"
							width={64}
							sx={{ margin: "0 auto" }}
							mb="md"
						/>
						<Text weight={600} align="center">
							Thank you!
						</Text>
						<Text mt="md" align="center">
							We have sent a verification email to{" "}
							<Anchor href={`mailto:${actionData.email}`} underline>
								{actionData.email}
							</Anchor>
						</Text>
						<Text mt="md" align="center">
							To reset your password please follow the steps provided in the
							email.
						</Text>
						<Text mt="md" align="center">
							Make sure to check your Spam folder since the email might have
							been caught by your spam filter.
						</Text>
						<Text mt="md" weight={600}>
							Regards,
						</Text>
						<Text>the Designers Trust team</Text>
					</Paper>
				) : (
					<Paper
						withBorder
						py="xl"
						px={{
							base: "md",
							md: 80,
						}}
					>
						<Image
							src="/images/DT_logo_circle.svg"
							alt="Designers Trust logo"
							width={64}
							sx={{ margin: "0 auto" }}
							mb="md"
						/>
						<Text weight={500} align="center">
							Forgot password
						</Text>
						<Text align="center">
							Enter your registration e-mail address and have your password
							changed
						</Text>
						<Box component={Form} method="post" mt="xl">
							<TextInput
								name="email"
								label="Email address"
								placeholder="Email address"
								error={
									actionData?.success === false
										? actionData.inputErrors?.email?.[0]
										: undefined
								}
							/>
							{actionData?.success === false && actionData.errors[0] && (
								<Alert color="red" icon={<IconAlertCircle size={16} />} mt="xl">
									{actionData.errors[0]}
								</Alert>
							)}
							<Button
								type="submit"
								color="green"
								fullWidth
								loading={navigation.state === "submitting"}
								mt="xl"
							>
								Submit
							</Button>
						</Box>
						<Group position="apart" mt="xl">
							<Anchor size="sm" component={Link} to="/login">
								<Text>Have an account? Log in</Text>
							</Anchor>
							<Anchor size="sm" component={Link} to="/register">
								<Text>Don&apos;t have an account?</Text>
							</Anchor>
						</Group>
					</Paper>
				)}
			</Container>
		</PublicLayout>
	);
}

export async function action({ request }: ActionArgs) {
	const input = await inputFromForm(request);
	const newPasswordResult = await requestNewPassword(input);

	if (!newPasswordResult.success) {
		return json(
			formatErrorMessagesForErrorResult(
				newPasswordResult,
				requestNewPasswordInputSchema,
			),
		);
	}

	return json({ success: true as const, email: input.email as string });
}
